"use client";
import React from "react";
import { BookOpen, Lock } from "lucide-react";
import Link from "next/link"; 

export default function TermsOfUsePage() {
  const appTitle = process.env.NEXT_PUBLIC_APP_TITLE ;
  const contactEmail = process.env.NEXT_PUBLIC_APP_EMAIL;
  const websiteUrl = process.env.NEXT_PUBLIC_APP_COMPANY_URL;

  return (
    <div className="lg:py-7 bg-gray-50">
      <div className="max-w-4xl mx-auto py-10 sm:py-16 lg:py-12 px-4 sm:px-6 lg:px-8 bg-white shadow-lg lg:rounded-xl">
        
        {/* Title and Separator */}
        <div className="mb-8 border-b pb-4 border-gray-200">
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-2 flex items-center">
            <BookOpen className="w-7 h-7 mr-3 text-primary-600 hidden sm:inline" />
            Terms of Use
          </h1>
        </div>
        
        <p className="text-base text-gray-700 mb-6 leading-relaxed"> 
          These terms and conditions of use apply to the {appTitle} website, {websiteUrl}, (referred to as the &apos;Website&apos;). By using this website, you agree to the below-mentioned Terms Of Use. If you do not agree, do not use this website or any of its services.
        </p>
        
        <p className="text-base text-gray-600 mb-8">
            {appTitle}reserves the absolute right to alter, add, modify, or remove some parts of these Terms and Conditions anytime at its sole discretion. It is the responsibility of the user to check our Terms Of Use from time to time. If you continue to use the website after the Terms and Conditions are updated, it implies that you agree with these changes. {appTitle} services include, without limitation – Coupons, Offers and Deals on Dining, Travel, Beauty, Lifestyle, Online Shopping, and much more.
        </p>

        {/* --- USE OF THE WEBSITE --- */}
        <h2 className="text-2xl font-bold text-gray-900 mb-4 pt-4 border-t border-gray-200">
          1. Use of the Website
        </h2>
        <p className="text-base text-gray-700 mb-4">
          Your right to use the website is restricted to your personal use only. Your use is subject to all state, national, and international laws and regulations. You agree:
        </p>
        <ul className="list-disc pl-6 text-base text-gray-700 space-y-2 mb-10">
          <li>To not use the website for any illegal or fraudulent purpose.</li>
          <li>To not post or upload any false, defamatory, infringing, misleading, or harmful information or content.</li>
          <li>To not use any data extraction or mining methods to use the data of the website.</li>
          <li>To not copy, duplicate the contentof the website until and unless specified.</li>
          <li>To not gain unauthorized access to any portion of the website by any illegal means.</li>
          <li>To not use the website for any commercial purposes without prior consent.</li>
          <li>To not remove any trademark or copyright notices from the website.</li>
          <li>To not input or upload any information which contains viruses, malware, adware, or any malicious code.</li>
          <li>To not use any device or software to interfere with the proper working of the website or any other user&apos;s use of the website.</li>
          <li>To not use any framing techniques to enclose or hide the trademark, logos, images, text, or page layout of the website.</li>
          <li>To not access website&apos;s property including content, images or website layout to create similar or competitive website.</li>
          <li>To not breach the security of the website.</li>
          <li>You will not use the website or any of its content for any unlawful or illegal purpose and for activities which are prohibited in the Terms of Use.</li>
        </ul>

        {/* --- INTELLECTUAL PROPERTY RIGHTS --- */}
        <h2 className="text-2xl font-bold text-gray-900 mb-4 pt-4 border-t border-gray-200">
          2. Intellectual Property Rights
        </h2>
        <p className="text-base text-gray-700 mb-6">
          All the content including text, images, software, sound, graphics, logos, artworks, design, and structure of the website and all other content and services made available to you on the website is owned and controlled by {appTitle} and licensed to {appTitle}. You are not permitted to use, distribute, display, translate, or copy this content in any form whatsoever. All the data and content on the website is protected by trademark, copyright, and patent rules and regulations.
        </p>

        {/* --- INFORMATION SUBMITTED BY USERS --- */}
        <h2 className="text-2xl font-bold text-gray-900 mb-4 pt-4 border-t border-gray-200">
          3. Information Submitted by Users
        </h2>
        <p className="text-base text-gray-700 mb-6">
          You agree that any content that is uploaded or information submitted by you, whether it is sent privately or posted publicly is solely your responsibility. You also warrant that the information furnished by you is accurate and true and you hold the right to provide that data or content. You are not allowed to post any content or information which is protected by trademark and copyright without any expressed written consent of the owner of that content. You are solely responsible for any dispute or copyright infringement arising due to the content or information you have provided.
        </p>
        
        {/* --- USER ACCOUNT AND PASSWORD --- */}
        <h2 className="text-2xl font-bold text-gray-900 mb-4 pt-4 border-t border-gray-200">
          4. User Account and Password
        </h2>
        
        <p className="text-base text-gray-700 mb-6">
          Some sections of the website can be browsed and viewed without any prior registration, but to gain access to personalized predictions, detailed analysis, reports and receive additional content, you need to sign up or register with {appTitle}. In order to register, you agree to provide complete and current information about yourself. Any misleading information can result in termination of your account.
        </p>
        
        <h3 className="text-xl font-semibold text-gray-800 mb-3">
          User ID and Password
        </h3>
        <p className="text-base text-gray-700 mb-6">
          When you create an account, it is your responsibility to maintain the confidentiality and security of your account credentials. You hold complete responsibility for the use of your account and agree to not transfer or sell your account to any other party. Also, in case of any breach of your account, you agree to notify {appTitle} on immediate basis. You also give your consent to {appTitle} and its employees or agents to access your account in order to provide you with better services or to investigate and solve issues, if any.
        </p>

        <h3 className="text-xl font-semibold text-gray-800 mb-3">
          Account Termination
        </h3>
        <p className="text-base text-gray-700 mb-3">
          {appTitle} reserves the right to modify or terminate your account or discontinue some of the services with or without any prior notice if:
        </p>
        <ul className="list-disc pl-6 text-base text-gray-700 space-y-2 mb-10">
            <li>You provide us misleading data or information.</li>
            <li>We are unable to authenticate or verify any information that is provided by you.</li>
            <li>You violate the Terms of Use.</li>
            <li>Your actions lead to any legal liability for us.</li>
        </ul>

        {/* --- PRIVACY --- */}
        <h2 className="text-2xl font-bold text-gray-900 mb-4 pt-4 border-t border-gray-200">
          5. Privacy
        </h2>
        <p className="text-base text-gray-700 mb-6">
          The Privacy Policy of {appTitle} applies to the website and the Terms of Use as well.
        </p>
        <div className="flex justify-center sm:justify-start mb-10">
            <Link 
                href="/privacy-policy" 
                className="flex items-center space-x-2 text-primary-600 font-semibold hover:text-primary-700 transition-colors w-max p-2 bg-primary-50 rounded-lg"
            >
                <Lock className="w-4 h-4"/>
                <span>Check our Privacy Policy</span>
            </Link>
        </div>

        {/* --- THIRD PARTY SERVICES --- */}
        <h2 className="text-2xl font-bold text-gray-900 mb-4 pt-4 border-t border-gray-200">
          6. Third Party Services
        </h2>
        <p className="text-base text-gray-700 mb-10">
          The website may contain links to other independent Third Party Websites. We do not hold any responsibility for the content, material or information published on these linked websites, as such sites are not in our control. It is entirely your sole discretion to browse or interact with these third party sites. {appTitle}, under any circumstance whatsoever, will not be liable for any damage caused to you due to your interaction or association with these websites.
        </p>

        {/* --- DISCLAIMER, WARRANTY, LIMITATION OF LIABILITY, INDEMNIFICATION --- */}
        <h2 className="text-2xl font-bold text-gray-900 mb-4 pt-4 border-t border-gray-200">
          7. Disclaimer, Warranty, and Liability
        </h2>
        <h3 className="text-xl font-semibold text-gray-800 mb-3">
          Disclaimer
        </h3>
        <p className="text-base text-gray-700 mb-6">
          The information on our website has been obtained from sources believed to be reliable. {appTitle} disclaims all warranties as to the accuracy, completeness of the materials or the reliability of any advice, statement, opinion or other information distributed through the website. You agree that any reliance on any such statement, opinion, advice or information shall be at your sole risk. {appTitle} reserves the right, in its sole discretion, to correct any errors or omissions and may change or discontinue any aspect of this website at any time without notice.
        </p>
        
        <h3 className="text-xl font-semibold text-gray-800 mb-3">
          Warranty
        </h3>
        <p className="text-base text-gray-700 mb-6">
          Although {appTitle} attempts to provide accurate content on the website, it makes no warranty that such content is accurate or suitable for any particular purpose or will meet your requirements or will be available on an uninterrupted, timely, secure, or error-free basis.
        </p>

        <h3 className="text-xl font-semibold text-gray-800 mb-3">
          Limitation of Liability
        </h3>
        <p className="text-base text-gray-700 mb-6">
          In no event will {appTitle} be liable for any damages, including without limitation, incidental, special, consequential or punitive damages, whether under a contract, tort or any other theory of liability, arising in connection with any party&apos;s use of the website or in connection with any failure of performance, error, omission, interruption, defect, delay in operation or transmission, computer virus, line system failure, loss of data, or loss of use related to this website or any contents of this website or any third-party.
        </p>
        
        <h3 className="text-xl font-semibold text-gray-800 mb-3">
          Indemnification
        </h3>
        <p className="text-base text-gray-700 mb-10">
          You agree to indemnify, defend and hold {appTitle} harmless from and against all expenses, claims, losses, damages, and costs, including reasonable attorney&apos;s fees, arising out of or relating to any misuse by the user of the content and services provided by the website.
        </p>

        {/* --- VIOLATION OF TERMS OF USE --- */}
        <h2 className="text-2xl font-bold text-gray-900 mb-4 pt-4 border-t border-gray-200">
          8. Violation of Terms of Use
        </h2>
        <p className="text-base text-gray-700 mb-6">
          You agree that {appTitle}, with or without prior notice, may terminate or restrict your access to the website or any other portion of the website if we find out that you have violated any of these terms and conditions. You agree that if you violate the termination or restriction, you will hold us harmless from any liability that we may incur. We reserve the right to persecute the violators as per the law.
        </p>

        {/* --- CONTACT DETAILS --- */}
        <h2 className="text-2xl font-bold text-gray-900 mb-4 pt-4 border-t border-gray-200">
          9. Contact Details
        </h2>
        <p className="text-base text-gray-700 mb-4">
          If you have any questions or need our assistance, you can reach out to us at:
        </p>
        <a 
            href={`mailto:${contactEmail}`} 
            className="text-primary-600 font-semibold hover:text-primary-700 underline transition-colors"
        >
            {contactEmail}
        </a>
      </div>
    </div>
  );
}